import {
  C_ON_SURFACE,
  C_ON_SURFACE_VARIANT,
  FontSize,
  getFontStyle,
  type Theme,
} from "@schaeffler-commerce/design-system";
import { GlobalStyles as MuiGlobalStyles } from "@schaeffler-commerce/design-system";

/**
 * Migrated from styles/cmsComponents/CMS.less.
 *
 * Cross-component CMS class rules that style injected/external CMS content
 * and shared CMS media wrappers:
 *
 * - `.scha__cms#contentStream` column layout (ContentStreamPageTemplate,
 *   ProductListPageTemplate).
 * - `.scha__cms` / `.scha__teaser` typography hooks: the `headline_h*_left`
 *   and `copy_left` classes are CMS-authored content classes (rendered via
 *   dangerouslySetInnerHTML), so these rules must stay global. The LESS
 *   selector also carried `.scha__cmssection` (bare) — dead, no renderer
 *   anywhere (React root, storefront views/tags, backend content; verified
 *   2026-09-06, only `scha__cmssection__*` subclasses render).
 * - `.scha__forced-ratio-image*` wrappers rendered by CMSMedia (and passed
 *   through by CMSTeaser's CMSMedia className).
 *
 * Teaser-owned pieces of CMS.less (frame, `--active`, `__lock` border,
 * `.scha__pipe` variants) moved to the CMSTeaser styled root instead;
 * `.scha__teasers__target p` was dead (class no longer rendered by
 * CMSTeasers) and is dropped.
 *
 * `headline_h*--label` uses C_ON_SURFACE_VARIANT (GREY_40 #646464) per the
 * SCHAEFF-39396 token ruling (0064 journal): the LESS sheet was stale on
 * GREY_30 (#555555). No committed visual baseline renders `--label`.
 */
export const cmsContentStyles = (_theme: Theme) => ({
  ".scha__cms#contentStream, .scha__cms#contentStream > .ContentStreamSlot": {
    display: "flex",
    flexDirection: "column",
    gap: "64px",
  },

  ".scha__cms, .scha__teaser": {
    display: "block",
    ...getFontStyle(FontSize.BodyLarge),

    "& .headline_h1_left": {
      ...getFontStyle(FontSize.HeadlineLarge),
      "&.headline_h1_left--default": {
        color: C_ON_SURFACE,
      },
      "&.headline_h1_left--label": {
        color: C_ON_SURFACE_VARIANT,
      },
    },

    "& .headline_h2_left": {
      ...getFontStyle(FontSize.HeadlineMedium),
      "&.headline_h2_left--default": {
        color: C_ON_SURFACE,
        fontWeight: 700,
      },
      "&.headline_h2_left--label": {
        color: C_ON_SURFACE_VARIANT,
      },
    },

    "& .headline_h3_left": {
      ...getFontStyle(FontSize.HeadlineSmall),
      "&.headline_h3_left--default": {
        color: C_ON_SURFACE,
      },
      "&.headline_h3_left--label": {
        color: C_ON_SURFACE_VARIANT,
      },
    },

    "& .headline_h4_left": {
      ...getFontStyle(FontSize.TitleLarge),
      fontWeight: "bold",
      "&.headline_h4_left--default": {
        color: C_ON_SURFACE,
      },
      "&.headline_h4_left--label": {
        color: C_ON_SURFACE_VARIANT,
      },
    },

    "& .headline_h5_left": {
      ...getFontStyle(FontSize.TitleMedium),
      fontWeight: "bold",
      "&.headline_h5_left--default": {
        color: C_ON_SURFACE,
      },
    },

    "& .headline_h6_left": {
      ...getFontStyle(FontSize.TitleSmall),
      fontWeight: "bold",
      "&.headline_h6_left--default": {
        color: C_ON_SURFACE,
      },
      "&.headline_h6_left--label": {
        color: C_ON_SURFACE_VARIANT,
      },
    },
  },

  ".scha__forced-ratio-image": {
    position: "relative",
    overflow: "hidden",
    borderRadius: "12px",
  },

  ".scha__forced-ratio-image-with-border-radius": {
    borderRadius: "12px",
  },

  ".scha__forced-ratio-image-circular": {
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    display: "flex",
    margin: "auto",
    marginTop: "50px",
    border: "1px solid #ccc",
    objectFit: "cover",
  },

  ".scha__forced-ratio-image--w": {
    width: "100%",
    height: "initial",
  },

  ".scha__forced-ratio-image--h": {
    width: "initial",
    height: "100%",
  },
});

export const CmsContentGlobalStyles = () => <MuiGlobalStyles styles={cmsContentStyles} />;
